// frontend/src/pages/CreatorHub.jsx
// ✅ UPDATED: Integrated with new /api/creator/styles/upload endpoint
// ✅ v3: Creator Agreement now covers video/animation royalties
import React from "react";
import { Link } from "react-router-dom";
import Screen from "../uizard/ui/Screen";
import { getIdentityHeaders, waitForIdentity } from "../lib/identity";
import { API_BASE, toast } from "../lib/api";

const api = (path) => API_BASE ? `${API_BASE}${path}` : path;

const CREATOR_AGREEMENT_VERSION = "v3";
const LS_KEY = `creatorAgreement.${CREATOR_AGREEMENT_VERSION}.acceptedAt`;

// Creator Program signup switch. While false, the submission wizard is
// replaced with a "temporarily closed" notice. The backend enforces this
// too (CREATOR_SIGNUP_DISABLED) — this is the matching UX so users don't
// see a form that would 403. Reopen by flipping to true (and clearing the
// backend env flag) once A2U payouts are live next quarter.
const CREATOR_SIGNUP_OPEN = true;

const brand = {
  purple: "#6D5EF9",
  purpleDark: "#5B4EE8",
  purpleTint: "#F3F0FF",
  purpleBorder: "#E7E2FF",
  neutralBorder: "#E5E7EB",
  neutralText: "#111827",
  muted: "#6B7280",
  success: "#10B981",
  danger: "#EF4444",
};

export default function CreatorHub() {
  const [step, setStep] = React.useState(1);
  const [name, setName] = React.useState("");
  const [desc, setDesc] = React.useState("");
  const [file, setFile] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(false);

  const [showAgreement, setShowAgreement] = React.useState(false);
  const [acceptedAt, setAcceptedAt] = React.useState(() => {
    try {
      const v = localStorage.getItem(LS_KEY);
      return v ? new Date(v).toISOString() : "";
    } catch {
      return "";
    }
  });
  const accepted = Boolean(acceptedAt);

  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState({ type: "", text: "" });
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [trainingJobId, setTrainingJobId] = React.useState(null);

  const fileInputRef = React.useRef(null);

  React.useEffect(() => {
    waitForIdentity();
  }, []);

  function pickFile() {
    if (busy) return;
    fileInputRef.current?.click();
  }

  function onFileChange(e) {
    const f = e.target.files?.[0];
    if (f) validateAndSetFile(f);
  }

  function validateAndSetFile(f) {
    setMsg({ type: "", text: "" });
    setTrainingJobId(null);

    const isZip =
      f.type === "application/zip" ||
      f.name.toLowerCase().endsWith(".zip") ||
      f.type === "application/x-zip-compressed";
    if (!isZip) {
      setMsg({ type: "error", text: "Please upload a .zip file." });
      toast("Please upload a .zip file");
      return;
    }
    const maxBytes = 100 * 1024 * 1024;
    if (f.size > maxBytes) {
      setMsg({ type: "error", text: "ZIP is too large (max ~100MB)." });
      toast("ZIP is too large (max ~100MB)");
      return;
    }
    setFile(f);
    toast("File selected");
  }

  function onDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (busy) return;
    const f = e.dataTransfer.files?.[0];
    if (f) validateAndSetFile(f);
  }

  function onDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!busy) setDragOver(true);
  }
  function onDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }

  async function submit() {
    if (!name.trim() || !desc.trim() || !file || !accepted) return;
    setBusy(true);
    setMsg({ type: "", text: "" });
    setUploadProgress(0);
    setTrainingJobId(null);

    try {
      const fd = new FormData();
      fd.append("file", file);

      const res = await fetch(api("/api/creator/styles/upload"), {
        method: "POST",
        body: fd,
        credentials: "include",
        headers: { ...getIdentityHeaders() }
      });

      const text = await res.text();
      let j;
      try { j = JSON.parse(text); } catch { j = null; }

      if (!res.ok) {
        const err = j?.error || text || "Failed to upload style pack";
        throw new Error(err);
      }

      const jobId = j?.trainingJobId;
      const imageCount = j?.imageCount || 0;
      const successMsg = j?.message || `Successfully uploaded ${imageCount} images. Training job queued.`;

      setTrainingJobId(jobId);
      setMsg({ type: "success", text: successMsg });
      toast(`✅ ${successMsg}`);

      setFile(null);

    } catch (e) {
      const errorMsg = e.message || "Submission failed.";
      setMsg({ type: "error", text: errorMsg });
      toast(`❌ ${errorMsg}`);
    } finally {
      setBusy(false);
      setUploadProgress(0);
    }
  }

  function openAgreement() { setShowAgreement(true); }
  function closeAgreement() { setShowAgreement(false); }

  async function markAccepted() {
    const ts = new Date().toISOString();

    // Record on the server FIRST — only treat it as accepted if that succeeds,
    // so the UI can never claim agreement that wasn't actually persisted.
    let res;
    try {
      res = await fetch(api("/api/creator/accept-terms"), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...getIdentityHeaders()
        },
        body: JSON.stringify({ version: CREATOR_AGREEMENT_VERSION, acceptedAt: ts }),
      });
    } catch (e) {
      console.error("[creator] accept-terms request failed:", e);
      toast("Couldn't save your acceptance — check your connection and try again");
      return;
    }

    if (!res.ok) {
      console.error("[creator] accept-terms rejected:", res.status);
      toast(`Couldn't save your acceptance (${res.status}) — please try again`);
      return;
    }

    try { localStorage.setItem(LS_KEY, ts); } catch {}
    setAcceptedAt(ts);
    setShowAgreement(false);
    toast("Agreement accepted");
  }

  const canGoToStep2 = accepted;
  const canGoToStep3 = name.trim() && desc.trim();
  const canSubmit = file && !busy;

  // Creator Program temporarily closed — show a notice instead of the wizard.
  if (!CREATOR_SIGNUP_OPEN) {
    return (
      <Screen>
        <div className="wizard-container">
          <header className="page-header">
            <h1 className="page-title">Royalty Program</h1>
            <p className="page-subtitle">Creator submissions are temporarily closed</p>
          </header>
          <div
            className="wizard-card"
            style={{ textAlign: "center", padding: "32px 24px" }}
          >
            <div style={{ fontSize: 40, marginBottom: 12 }}>🎨</div>
            <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 10, color: brand.neutralText }}>
              The Creator Program will reopen soon
            </h2>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: brand.muted, maxWidth: 460, margin: "0 auto" }}>
              We're putting the finishing touches on creator royalty payouts.
              New style submissions are paused for now and will reopen shortly —
              thanks for your interest in becoming a creator. In the meantime,
              you can keep generating and exploring styles as usual.
            </p>
            <div style={{ marginTop: 20 }}>
              <Link to="/create" className="policy-link" style={{ fontWeight: 700, color: brand.purple }}>
                Go to Create →
              </Link>
            </div>
          </div>
        </div>
      </Screen>
    );
  }

  return (
    <Screen>
      <StyleTag />

      <div className="wizard-container">
        <header className="page-header">
          <h1 className="page-title">Royalty Program</h1>
          <p className="page-subtitle">
            Submit Style • Earn royalties • Withdraw to wallet
          </p>
          <p className="page-hint">
            Submissions are now open! Prepare your style pack now.
          </p>
        </header>

        <div className="progress-bar">
          <div className={`progress-step ${step >= 1 ? "active" : ""} ${step > 1 ? "complete" : ""}`}>
            <div className="step-circle">{step > 1 ? "✓" : "1"}</div>
            <div className="step-label">Accept Terms</div>
          </div>
          <div className={`progress-line ${step > 1 ? "complete" : ""}`} />
          <div className={`progress-step ${step >= 2 ? "active" : ""} ${step > 2 ? "complete" : ""}`}>
            <div className="step-circle">{step > 2 ? "✓" : "2"}</div>
            <div className="step-label">Style Details</div>
          </div>
          <div className={`progress-line ${step > 2 ? "complete" : ""}`} />
          <div className={`progress-step ${step >= 3 ? "active" : ""}`}>
            <div className="step-circle">3</div>
            <div className="step-label">Upload</div>
          </div>
        </div>

        <div className="wizard-card">
          {step === 1 && (
            <Step1
              accepted={accepted}
              acceptedAt={acceptedAt}
              onOpenAgreement={openAgreement}
              onNext={() => setStep(2)}
              canNext={canGoToStep2}
            />
          )}

          {step === 2 && (
            <Step2
              name={name}
              setName={setName}
              desc={desc}
              setDesc={setDesc}
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
              canNext={canGoToStep3}
            />
          )}

          {step === 3 && (
            <Step3
              file={file}
              dragOver={dragOver}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              pickFile={pickFile}
              fileInputRef={fileInputRef}
              onFileChange={onFileChange}
              onBack={() => setStep(2)}
              onSubmit={submit}
              canSubmit={canSubmit}
              busy={busy}
              msg={msg}
              uploadProgress={uploadProgress}
              trainingJobId={trainingJobId}
            />
          )}
        </div>
      </div>

      {showAgreement && (
        <AgreementModal
          version={CREATOR_AGREEMENT_VERSION}
          onClose={closeAgreement}
          onAccept={markAccepted}
        />
      )}
    </Screen>
  );
}

/* ============================= STEP 1 ============================= */
function Step1({ accepted, acceptedAt, onOpenAgreement, onNext, canNext }) {
  return (
    <div className="step-content">
      <h2 className="step-title">Accept Terms</h2>
      <p className="step-intro">Review the program details and accept the agreement to continue</p>

      <div className="info-cards">
        <div className="info-card highlight">
          <div className="info-label">Royalty Rate</div>
          <div className="info-value">30%</div>
          <div className="info-hint">Fixed program rate</div>
        </div>

        <div className="info-card">
          <div className="info-label">Training Time</div>
          <div className="info-value">Up to 5 days</div>
          <div className="info-hint">Average processing time</div>
        </div>

        <div className="info-card">
          <div className="info-label">File Format</div>
          <div className="info-value">.zip</div>
          <div className="info-hint">Min 20 images</div>
        </div>
      </div>

      {/* ✅ FROM GOOGLE: Content policy link box */}
      <div className="policy-link-box">
        <div className="policy-link-text">
          Please review our <Link to="/content-policy" className="policy-link">Content Policy</Link> before submitting.
          Violations may result in style removal and account suspension.
        </div>
      </div>

      <div className={`agreement-box ${accepted ? "accepted" : ""}`}>
        <div className="agreement-status">
          {accepted ? (
            <>
              <span className="status-icon">✓</span>
              <span className="status-text">Agreement accepted</span>
              <span className="status-date">{new Date(acceptedAt).toLocaleDateString()}</span>
            </>
          ) : (
            <>
              <span className="status-icon">⚠️</span>
              <span className="status-text">Agreement required</span>
            </>
          )}
        </div>
        <button className="btn btn-sm" onClick={onOpenAgreement}>
          {accepted ? "Review Agreement" : "Read & Accept Agreement"}
        </button>
      </div>

      <div className="step-nav">
        <div />
        <button
          className="btn btn-primary"
          onClick={onNext}
          disabled={!canNext}
          title={!canNext ? "Please accept the agreement to continue" : ""}
        >
          Continue to Details →
        </button>
      </div>
    </div>
  );
}

/* ============================= STEP 2 ============================= */
function Step2({ name, setName, desc, setDesc, onBack, onNext, canNext }) {
  return (
    <div className="step-content">
      <h2 className="step-title">Style Details</h2>
      <p className="step-intro">Provide a name and description for your style pack</p>

      <label className="label">Style name *</label>
      <input
        className="input"
        placeholder="e.g., Gloom Pop"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={60}
      />
      <div className="hint-text">{name.length}/60 characters</div>

      <label className="label">Description *</label>
      {/* ✅ FROM GOOGLE: maxLength + character counter */}
      <textarea
        className="textarea"
        rows={5}
        placeholder="Describe the look, mood, colors, shapes, subject matter, and ideal prompts..."
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        maxLength={1500}
      />
      <div className="hint-text">{desc.length}/1500 characters</div>

      <Collapsible title="Writing Tips">
        <ul className="tips-list">
          <li><b>Be descriptive:</b> "Candy-lit mushroom forests with bold palettes" not "colorful art"</li>
          <li><b>Mention subjects:</b> What works well? Portraits, landscapes, products?</li>
          <li><b>Suggest prompts:</b> Give 2-3 example prompts that showcase the style</li>
          <li><b>Note limitations:</b> What doesn't work? "Not ideal for photorealism"</li>
        </ul>
      </Collapsible>

      <div className="step-nav">
        <button className="btn" onClick={onBack}>
          ← Back
        </button>
        <button
          className="btn btn-primary"
          onClick={onNext}
          disabled={!canNext}
          title={!canNext ? "Please fill in all required fields" : ""}
        >
          Continue to Upload →
        </button>
      </div>
    </div>
  );
}

/* ============================= STEP 3 ============================= */
// ✅ REMOVED: Countdown timer — submissions are open, always show submit button
function Step3({ file, dragOver, onDrop, onDragOver, onDragLeave, pickFile, fileInputRef, onFileChange, onBack, onSubmit, canSubmit, busy, msg, uploadProgress, trainingJobId }) {
  return (
    <div className="step-content">
      <h2 className="step-title">Upload Style Pack</h2>
      <p className="step-intro">Upload your ZIP file containing at least 20 images</p>

      <label className="label">ZIP file *</label>
      <div
        className={`drop ${dragOver ? "over" : ""}`}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={pickFile}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" ? pickFile() : null)}
      >
        <div className="drop-inner">
          <div className="drop-title">Drag & drop ZIP here</div>
          <div className="drop-sub">or click to choose a file</div>
          <div className="drop-meta">
            .zip format • Max ~100MB • <b>Min 20 images</b> (JPG/PNG/WebP)
          </div>
          {file && (
            <div className="file-pill">
              ✓ {file.name} <span>({prettyBytes(file.size)})</span>
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".zip,application/zip,application/x-zip-compressed"
          style={{ display: "none" }}
          onChange={onFileChange}
        />
      </div>

      <Collapsible title="Good Pack Examples">
        <ul className="tips-list">
          <li><b>Portrait pack:</b> 20 close-ups, consistent lighting, similar palette</li>
          <li><b>Product style:</b> Same background, similar angles—great for mockups</li>
          <li><b>Illustration:</b> Matching line weight and color language across all images</li>
        </ul>
        <div className="example-note">
          <b>Quality matters:</b> Remove duplicates and low-res images. Cohesion = better model + more earnings.
        </div>
      </Collapsible>

      {busy && uploadProgress > 0 && (
        <div className="progress-container">
          <div className="progress-label">Uploading... {uploadProgress}%</div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
          </div>
        </div>
      )}

      {msg.text && (
        <div className={`notice ${msg.type === "error" ? "error" : "success"}`}>
          <div className="notice-text">{msg.text}</div>
          {trainingJobId && (
            <div className="training-job-info">
              <div className="job-label">Training Job ID:</div>
              <div className="job-id">{trainingJobId}</div>
              <div className="job-hint">Save this ID to check your training status</div>
            </div>
          )}
        </div>
      )}

      <div className="step-nav">
        <button className="btn" onClick={onBack} disabled={busy}>
          ← Back
        </button>
        <button
          className="btn btn-primary"
          onClick={onSubmit}
          disabled={!canSubmit}
        >
          {busy ? "Uploading…" : "Submit for Training"}
        </button>
      </div>

      <div className="final-note">
        {trainingJobId ? (
          <>Training job created! Processing will begin shortly. You can safely navigate away.</>
        ) : (
          <>Submitting will upload your ZIP and queue training. You can navigate away safely after submission.</>
        )}
      </div>
    </div>
  );
}

/* ============================= COLLAPSIBLE ============================= */
function Collapsible({ title, children, defaultOpen = false }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="collapsible">
      <button className="collapsible-trigger" onClick={() => setOpen(!open)}>
        <span>{title}</span>
        <span className="collapsible-icon">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="collapsible-content">{children}</div>}
    </div>
  );
}

/* ============================= UTILS ============================= */
function prettyBytes(b) {
  if (!Number.isFinite(b)) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/* ============================= AGREEMENT MODAL ============================= */
function AgreementModal({ version, onClose, onAccept }) {
  const contentRef = React.useRef(null);
  const [scrolledToEnd, setScrolledToEnd] = React.useState(false);
  const [checked, setChecked] = React.useState(false);

  function onScroll() {
    const el = contentRef.current;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    if (nearBottom) setScrolledToEnd(true);
  }

  React.useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const canAccept = scrolledToEnd && checked;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <header className="modal-head">
          <div>
            <h3 style={{ margin: 0, fontWeight: 900 }}>Creator Commercial License & Agreement</h3>
            <div style={{ color: brand.muted, fontSize: 13 }}>Version {version}</div>
          </div>
          <button className="btn btn-sm" onClick={onClose}>Close</button>
        </header>

        <div className="modal-body">
          <div className="scroll" ref={contentRef} onScroll={onScroll}>
            <p><strong>Summary (non-binding):</strong> You grant ArtiBytes a non-exclusive worldwide license to use
              submitted assets to train, host, and serve a Style for commercial use on ArtiBytes. Your Style may be used
              to generate both images and videos (animations). You warrant you own or control all rights to submitted
              content. You will receive royalties on paid generations and paid animations that use your Style, as described
              in the Creator Program, subject to policy and safety review. The summary is for convenience only; the terms
              below are binding.</p>

            <h4 style={{ marginTop: 16 }}>ArtiBytes Creator Commercial Licensing Agreement</h4>
            <p><b>Effective Date:</b> The date you click "Accept & Continue."</p>

            <h4>1. Definitions</h4>
            <ul>
              <li><b>"ArtiBytes"</b> means the application and service operated by ArtiBytes.</li>
              <li><b>"Assets"</b> means any images, data, or materials you submit to ArtiBytes for training.</li>
              <li><b>"Style"</b> means a generative model configuration trained on your Assets and hosted by ArtiBytes.</li>
              <li><b>"Outputs"</b> means images and videos (animations) generated by end users using your Style.</li>
              <li><b>"Creator"</b> or <b>"You"</b> means the individual or entity submitting Assets.</li>
              <li><b>"Royalties"</b> means the share of revenue in Pi allocated to you under the Creator Program.</li>
            </ul>

            <h4>2. License Grant</h4>
            <p>You grant ArtiBytes a <b>non-exclusive, worldwide, transferable, sublicensable, royalty-bearing license</b> to
              reproduce, adapt, process, host, cache, train on, and transmit your Assets; create, maintain, and serve a Style
              based on your Assets; and allow end users to generate images and videos (animations) using your Style for
              commercial purposes. This license continues for as long as ArtiBytes hosts your Style, subject to termination
              below.</p>

            <h4>3. Ownership</h4>
            <ul>
              <li>You retain ownership of your Assets. This Agreement does not transfer ownership of your original works.</li>
              <li>ArtiBytes owns all rights in the trained model weights, system outputs, and derivative artifacts created by or within the ArtiBytes platform (excluding your original Assets).</li>
              <li>You acknowledge that multiple Styles may use similar concepts, and ArtiBytes has no obligation to prevent overlap between different Creators' Styles.</li>
            </ul>

            <h4>4. Rights, Restrictions & Safety</h4>
            <ul>
              <li>You represent and warrant that you have all necessary rights, licenses, and permissions to submit the Assets; your Assets do not infringe or misappropriate any third-party rights; and your submissions comply with applicable laws and ArtiBytes policies.</li>
              <li>No illegal, harmful, or restricted content; no third-party IP without permission.</li>
              <li>ArtiBytes may review, suspend, or remove any Style for policy, safety, or compliance reasons, at its sole discretion.</li>
            </ul>

            <h4>5. Royalties & Payouts</h4>
            <ul>
              <li>When users generate <b>paid images</b> or <b>paid videos (animations)</b> using your Style, you accrue Royalties in Pi, as described in the Creator Program. The same royalty rate applies whether the Output is an image or a video.</li>
              <li>Royalties are credited to your ArtiBytes account and payable to your Pi wallet once the <b>minimum payout threshold</b> is met.</li>
              <li>ArtiBytes may update payout schedules, thresholds, or program terms with <b>reasonable notice</b>.</li>
              <li>You are responsible for providing accurate wallet information; ArtiBytes is not responsible for failed payouts due to incorrect details or on-chain/network issues outside its control.</li>
            </ul>

            <h4>6. Term & Termination</h4>
            <ul>
              <li>Either party may terminate this Agreement by notice (including electronic notice within the product).</li>
              <li>Upon termination: (a) your Assets remain yours; (b) ArtiBytes may retain trained model artifacts as necessary to meet legal, compliance, audit, security, or operational obligations; and (c) accrued but unpaid Royalties remain payable per Section 5.</li>
              <li>Sections relating to ownership, royalties accrued but unpaid, disclaimers, indemnification, limitations of liability, and governing terms survive termination.</li>
            </ul>

            <h4>7. Privacy</h4>
            <p>
              ArtiBytes processes account, wallet, and submission data in accordance with its{" "}
              <Link to="/privacy" onClick={onClose}>Privacy Policy</Link>, which forms part of this Agreement.
            </p>

            <h4>8. Disclaimers & Liability</h4>
            <ul>
              <li>The ArtiBytes platform is provided <b>"as is"</b> without warranties of merchantability, fitness for a particular purpose, or non-infringement.</li>
              <li>To the maximum extent permitted by law, ArtiBytes' aggregate liability to you for any claim is limited to the total Royalties paid or payable to you in the <b>12 months</b> preceding the event giving rise to the claim.</li>
            </ul>

            <h4>9. Indemnification</h4>
            <p>You agree to indemnify and hold harmless ArtiBytes, its affiliates, and personnel from and against any claims,
              damages, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or related to your
              Assets, your breach of this Agreement, or any infringement or violation of third-party rights.</p>

            <h4>10. Governing Terms</h4>
            <p>
              This Agreement supplements the ArtiBytes{" "}
              <Link to="/terms" onClick={onClose}>Terms of Service</Link> and{" "}
              <Link to="/content-policy" onClick={onClose}>Content Policy</Link>.
              In case of conflict, this Agreement controls for Creator submissions.
            </p>

            <p style={{ marginTop: 24, color: brand.muted }}>
              End of Agreement. Scroll to the bottom to enable acceptance.
            </p>
          </div>

          <div className="accept-row">
            {!scrolledToEnd && <div className="hint-text">Please scroll to the end to enable acceptance.</div>}
            <label className="checkline">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                disabled={!scrolledToEnd}
              />
              <span>I have read and agree to the Creator Commercial License & Agreement (v{version}).</span>
            </label>
          </div>
        </div>

        <footer className="modal-foot">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={onAccept} disabled={!canAccept}>
            Accept & Continue
          </button>
        </footer>
      </div>
    </div>
  );
}

/* ============================= STYLES ============================= */
function StyleTag() {
  return (
    <style>{`
/* Container */
.wizard-container{
  max-width:720px;
  margin:0 auto;
  padding:24px 4vw;
}
@media (max-width:640px){
  .wizard-container{ padding:16px max(16px, 4vw); }
}

/* Page Header */
.page-header{
  background:linear-gradient(145deg, #F3F0FF, #ffffff);
  border:1px solid ${brand.purpleBorder};
  border-radius:16px;
  padding:24px;
  margin-bottom:28px;
  box-shadow:0 4px 16px rgba(109,94,249,0.08);
}
@media (max-width:640px){
  .page-header{ padding:20px; }
}
.page-title{
  font-size:clamp(24px, 4vw, 32px);
  font-weight:900;
  margin:0 0 8px;
  background:linear-gradient(135deg, ${brand.purple} 0%, #1E3A8A 100%);
  -webkit-background-clip:text;
  background-clip:text;
  -webkit-text-fill-color:transparent;
  letter-spacing:-0.01em;
  line-height:1.1;
}
.page-subtitle{
  color:${brand.muted};
  margin:0 0 10px;
  font-size:15px;
  font-weight:600;
}
.page-hint{
  margin:0;
  padding:10px 14px;
  background:rgba(109,94,249,0.08);
  border-left:3px solid ${brand.purple};
  border-radius:8px;
  font-size:14px;
  color:${brand.neutralText};
  font-weight:600;
}

/* Progress Bar */
.progress-bar{
  display:flex;
  align-items:center;
  justify-content:center;
  margin-bottom:32px;
}
.progress-step{
  display:flex;
  flex-direction:column;
  align-items:center;
  gap:10px;
}
.step-circle{
  width:44px;
  height:44px;
  border-radius:999px;
  border:2px solid ${brand.neutralBorder};
  background:#fff;
  display:grid;
  place-items:center;
  font-weight:800;
  font-size:16px;
  color:${brand.muted};
  transition:all .4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:0 2px 8px rgba(0,0,0,.06);
  position:relative;
}
.step-circle::before{
  content:'';
  position:absolute;
  inset:-4px;
  border-radius:999px;
  background:linear-gradient(135deg, ${brand.purple}, #1E3A8A);
  opacity:0;
  transition:opacity .3s;
  z-index:-1;
}
.progress-step.active .step-circle{
  border-color:${brand.purple};
  background:linear-gradient(135deg, ${brand.purpleTint}, #ffffff);
  color:${brand.purple};
  transform:scale(1.05);
  box-shadow:0 4px 12px rgba(109,94,249,0.2);
}
.progress-step.active .step-circle::before{
  opacity:0.15;
}
.progress-step.complete .step-circle{
  border:none;
  background:linear-gradient(135deg, ${brand.purple}, #1E3A8A);
  color:#fff;
  box-shadow:0 4px 16px rgba(109,94,249,0.35);
}
.step-label{
  font-size:13px;
  font-weight:700;
  color:${brand.muted};
  transition:all .3s;
}
.progress-step.active .step-label{
  color:${brand.purple};
  font-weight:800;
}
.progress-line{
  width:60px;
  height:3px;
  background:${brand.neutralBorder};
  margin:0 8px;
  transition:all .4s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius:999px;
  position:relative;
  overflow:hidden;
}
.progress-line::before{
  content:'';
  position:absolute;
  inset:0;
  background:linear-gradient(90deg, ${brand.purple}, #1E3A8A);
  transform:translateX(-100%);
  transition:transform .4s cubic-bezier(0.4, 0, 0.2, 1);
}
.progress-line.complete::before{
  transform:translateX(0);
}
@media (max-width:640px){
  .progress-line{ width:40px; }
  .step-label{ font-size:11px; }
  .step-circle{ width:40px; height:40px; font-size:14px; }
}

/* Card */
.wizard-card{
  background:#fff;
  border:1px solid ${brand.neutralBorder};
  border-radius:18px;
  padding:32px;
  box-shadow:0 8px 24px rgba(0,0,0,.06), 0 2px 8px rgba(0,0,0,.04);
  position:relative;
}
.wizard-card::before{
  content:'';
  position:absolute;
  top:0;
  left:0;
  right:0;
  height:4px;
  background:linear-gradient(90deg, ${brand.purple}, #1E3A8A);
  border-radius:18px 18px 0 0;
}
@media (max-width:640px){
  .wizard-card{ padding:24px 20px; }
}

/* Step Content */
.step-content{ display:grid; gap:16px; }
.step-title{
  font-size:22px;
  font-weight:900;
  margin:0 0 4px;
  color:${brand.neutralText};
}
.step-intro{
  font-size:15px;
  color:${brand.muted};
  margin:0 0 8px;
  font-weight:600;
}

/* Info Cards */
.info-cards{
  display:grid;
  grid-template-columns:repeat(3, 1fr);
  gap:14px;
  margin:16px 0;
}
@media (max-width:640px){
  .info-cards{ grid-template-columns:1fr; gap:12px; margin:12px 0; }
}
.info-card{
  background:linear-gradient(145deg, #ffffff, #f9fafb);
  border:1px solid ${brand.neutralBorder};
  border-radius:14px;
  padding:20px 18px;
  text-align:center;
  position:relative;
  overflow:hidden;
  transition:all .3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:0 2px 8px rgba(0,0,0,.04);
}
.info-card::before{
  content:'';
  position:absolute;
  top:0;
  left:0;
  right:0;
  height:3px;
  background:linear-gradient(90deg, ${brand.neutralBorder}, ${brand.neutralBorder});
  transition:all .3s;
}
.info-card:hover{
  transform:translateY(-2px);
  box-shadow:0 8px 20px rgba(0,0,0,.08);
  border-color:${brand.purple};
}
.info-card:hover::before{
  background:linear-gradient(90deg, ${brand.purple}, #1E3A8A);
}
.info-card.highlight{
  background:linear-gradient(145deg, ${brand.purpleTint}, #ffffff);
  border-color:${brand.purpleBorder};
  box-shadow:0 4px 16px rgba(109,94,249,0.15);
}
.info-card.highlight::before{
  background:linear-gradient(90deg, ${brand.purple}, #1E3A8A);
}
.info-card.highlight:hover{
  box-shadow:0 8px 24px rgba(109,94,249,0.25);
}
.info-label{
  font-size:11px;
  color:${brand.muted};
  font-weight:800;
  text-transform:uppercase;
  letter-spacing:0.08em;
  margin-bottom:8px;
}
.info-value{
  font-size:32px;
  font-weight:900;
  color:${brand.neutralText};
  margin-bottom:6px;
  letter-spacing:-0.02em;
}
.info-card.highlight .info-value{
  background:linear-gradient(135deg, ${brand.purple}, #1E3A8A);
  -webkit-background-clip:text;
  background-clip:text;
  -webkit-text-fill-color:transparent;
}
.info-hint{
  font-size:13px;
  color:${brand.muted};
  font-weight:600;
}

/* Policy Link Box */
.policy-link-box{
  padding:14px 16px;
  background:linear-gradient(145deg, #F3F0FF, #ffffff);
  border:1px solid ${brand.purpleBorder};
  border-left:4px solid ${brand.purple};
  border-radius:12px;
  margin:4px 0;
}
.policy-link-text{
  font-size:14px;
  line-height:1.6;
  color:${brand.neutralText};
  font-weight:600;
}
.policy-link{
  color:${brand.purple};
  font-weight:800;
  text-decoration:none;
  border-bottom:2px solid ${brand.purpleBorder};
  transition:all 0.2s;
}
.policy-link:hover{
  color:${brand.purpleDark};
  border-bottom-color:${brand.purple};
}

/* Collapsible */
.collapsible{
  border:1px solid ${brand.neutralBorder};
  border-radius:14px;
  overflow:hidden;
  margin:4px 0;
  background:#fff;
  box-shadow:0 2px 8px rgba(0,0,0,.04);
  transition:all .3s;
}
.collapsible:hover{
  box-shadow:0 4px 12px rgba(0,0,0,.08);
  border-color:${brand.purple};
}
.collapsible-trigger{
  width:100%;
  display:flex;
  align-items:center;
  justify-content:space-between;
  padding:16px 18px;
  background:linear-gradient(145deg, #fafafa, #ffffff);
  border:0;
  font-weight:800;
  font-size:15px;
  cursor:pointer;
  transition:all .2s;
  color:${brand.neutralText};
}
.collapsible-trigger:hover{
  background:linear-gradient(145deg, #f5f5f7, #fafafa);
}
.collapsible-icon{
  width:24px;
  height:24px;
  display:grid;
  place-items:center;
  font-size:20px;
  color:${brand.purple};
  font-weight:700;
  border-radius:6px;
  background:${brand.purpleTint};
  transition:all .3s;
}
.collapsible-trigger:hover .collapsible-icon{
  background:${brand.purple};
  color:#fff;
  transform:rotate(90deg);
}
.collapsible-content{
  padding:18px;
  background:#fff;
  border-top:1px solid ${brand.neutralBorder};
}

/* Lists */
.policy-list, .tips-list{
  margin:0;
  padding-left:20px;
  display:grid;
  gap:8px;
  line-height:1.6;
}
.example-note{
  margin-top:12px;
  padding:12px 14px;
  background:linear-gradient(145deg, #f9fafb, #ffffff);
  border:1px solid ${brand.neutralBorder};
  border-left:4px solid ${brand.purple};
  border-radius:10px;
  font-size:14px;
  font-weight:600;
}

/* Agreement Box */
.agreement-box{
  padding:16px;
  border:2px dashed ${brand.neutralBorder};
  border-radius:14px;
  background:linear-gradient(145deg, #fafafa, #ffffff);
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  flex-wrap:wrap;
  box-shadow:0 2px 8px rgba(0,0,0,.04);
  transition:all .3s;
}
.agreement-box:hover{
  border-color:${brand.purple};
}
.agreement-box.accepted{
  background:linear-gradient(145deg, #f0fdf4, #ffffff);
  border-color:#86efac;
  border-style:solid;
  box-shadow:0 4px 12px rgba(16,185,129,0.15);
}
.agreement-status{
  display:flex;
  align-items:center;
  gap:12px;
}
.status-icon{
  width:28px;
  height:28px;
  display:grid;
  place-items:center;
  font-size:16px;
  border-radius:8px;
  background:${brand.purpleTint};
  color:${brand.purple};
  font-weight:800;
}
.agreement-box.accepted .status-icon{
  background:#d1fae5;
  color:#059669;
}
.status-text{
  font-weight:800;
  font-size:15px;
  color:${brand.neutralText};
}
.status-date{
  font-size:13px;
  color:${brand.muted};
  font-weight:600;
}
@media (max-width:640px){
  .agreement-box{ flex-direction:column; align-items:flex-start; }
  .agreement-box .btn{ width:100%; }
}

/* Inputs */
.label{
  display:block;
  font-weight:800;
  margin:16px 0 8px;
  font-size:15px;
}
.input{
  width:100%;
  height:44px;
  border:1px solid ${brand.neutralBorder};
  border-radius:10px;
  padding:10px 14px;
  outline:none;
  font-size:15px;
}
.textarea{
  width:100%;
  border:1px solid ${brand.neutralBorder};
  border-radius:10px;
  padding:12px 14px;
  outline:none;
  resize:vertical;
  font-size:15px;
  line-height:1.5;
}
.input:focus, .textarea:focus{
  border-color:${brand.purple};
  box-shadow:0 0 0 3px ${brand.purpleTint};
}
.hint-text{
  font-size:13px;
  color:${brand.muted};
  margin-top:6px;
}

/* Dropzone */
.drop{
  margin-top:8px;
  border:2px dashed ${brand.neutralBorder};
  border-radius:14px;
  padding:40px 24px;
  cursor:pointer;
  background:linear-gradient(145deg, #fafafa, #ffffff);
  transition:all .3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:0 2px 8px rgba(0,0,0,.04);
  position:relative;
}
.drop::before{
  content:'';
  position:absolute;
  inset:0;
  border-radius:14px;
  padding:2px;
  background:linear-gradient(135deg, ${brand.purple}, #1E3A8A);
  -webkit-mask:linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite:xor;
  mask-composite:exclude;
  opacity:0;
  transition:opacity .3s;
}
.drop:hover{
  border-color:${brand.purple};
  transform:translateY(-2px);
  box-shadow:0 8px 20px rgba(109,94,249,0.15);
}
.drop:hover::before{
  opacity:1;
}
.drop.over{
  border-color:${brand.purple};
  background:linear-gradient(145deg, ${brand.purpleTint}, #ffffff);
  box-shadow:0 0 0 4px ${brand.purpleTint}, 0 8px 20px rgba(109,94,249,0.2);
  transform:translateY(-2px);
}
.drop-inner{ text-align:center; color:${brand.muted}; }
.drop-title{
  font-weight:800;
  color:${brand.neutralText};
  margin-bottom:8px;
  font-size:18px;
}
.drop-sub{
  font-size:14px;
  margin-bottom:10px;
  color:${brand.muted};
}
.drop-meta{
  font-size:13px;
  color:${brand.muted};
  font-weight:600;
}
.file-pill{
  display:inline-flex;
  gap:10px;
  align-items:center;
  margin-top:18px;
  background:#fff;
  border:1px solid ${brand.purpleBorder};
  border-radius:999px;
  padding:10px 18px;
  font-weight:700;
  font-size:14px;
  color:${brand.purple};
  box-shadow:0 2px 8px rgba(109,94,249,0.15);
}

/* Upload Progress Bar */
.progress-container{
  margin:16px 0;
  padding:14px;
  background:linear-gradient(145deg, ${brand.purpleTint}, #ffffff);
  border:1px solid ${brand.purpleBorder};
  border-radius:12px;
}
.progress-label{
  font-size:14px;
  font-weight:800;
  color:${brand.purple};
  margin-bottom:10px;
}
.progress-track{
  height:8px;
  background:rgba(109,94,249,0.15);
  border-radius:999px;
  overflow:hidden;
}
.progress-fill{
  height:100%;
  background:linear-gradient(90deg, ${brand.purple}, #1E3A8A);
  border-radius:999px;
  transition:width .3s;
}

/* Buttons */
.btn{
  height:44px;
  padding:0 20px;
  border-radius:12px;
  border:1px solid ${brand.neutralBorder};
  background:#fff;
  font-weight:800;
  font-size:15px;
  cursor:pointer;
  transition:all .2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:0 1px 3px rgba(0,0,0,.08);
}
.btn:hover:not(:disabled){
  background:#f9fafb;
  transform:translateY(-1px);
  box-shadow:0 4px 8px rgba(0,0,0,.12);
}
.btn.btn-sm{
  height:36px;
  padding:0 16px;
  font-size:14px;
  border-radius:10px;
}
.btn-primary{
  background:linear-gradient(135deg, ${brand.purple} 0%, #1E3A8A 100%);
  color:#fff;
  border:none;
  box-shadow:0 4px 12px rgba(109,94,249,0.3);
  position:relative;
  overflow:hidden;
}
.btn-primary::before{
  content:'';
  position:absolute;
  inset:0;
  background:linear-gradient(135deg, ${brand.purpleDark} 0%, #16306E 100%);
  opacity:0;
  transition:opacity .2s;
}
.btn-primary:hover:not(:disabled){
  transform:translateY(-2px);
  box-shadow:0 8px 20px rgba(109,94,249,0.4);
}
.btn-primary:hover:not(:disabled)::before{
  opacity:1;
}
.btn-primary > *{
  position:relative;
  z-index:1;
}
.btn[disabled]{
  opacity:.5;
  cursor:not-allowed;
  transform:none !important;
  box-shadow:0 1px 3px rgba(0,0,0,.08) !important;
}

/* Step Navigation */
.step-nav{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  margin-top:20px;
}
@media (max-width:640px){
  .step-nav{ flex-direction:column-reverse; }
  .step-nav .btn{ width:100%; }
}
.final-note{
  font-size:13px;
  color:${brand.muted};
  text-align:center;
  margin-top:8px;
}

/* Notice */
.notice{
  margin:12px 0;
  padding:16px;
  border-radius:12px;
  font-size:14px;
}
.notice.error{
  color:#991b1b;
  background:#fee2e2;
  border:1px solid #fca5a5;
}
.notice.success{
  color:#065f46;
  background:#d1fae5;
  border:1px solid #6ee7b7;
}
.notice-text{
  font-weight:700;
  margin-bottom:8px;
}
.training-job-info{
  margin-top:12px;
  padding:12px;
  background:rgba(255,255,255,0.8);
  border-radius:8px;
  border:1px solid rgba(16,185,129,0.3);
}
.job-label{
  font-size:12px;
  color:${brand.muted};
  font-weight:800;
  text-transform:uppercase;
  letter-spacing:0.05em;
  margin-bottom:6px;
}
.job-id{
  font-family:monospace;
  font-size:14px;
  font-weight:700;
  color:${brand.neutralText};
  padding:8px;
  background:#f9fafb;
  border-radius:6px;
  word-break:break-all;
  margin-bottom:6px;
}
.job-hint{
  font-size:12px;
  color:${brand.muted};
  font-style:italic;
}

/* Modal */
.modal-backdrop{
  position:fixed;
  inset:0;
  background:rgba(0,0,0,.5);
  display:grid;
  place-items:center;
  z-index:100;
  padding:16px;
}
.modal{
  width:min(800px, 100%);
  background:#fff;
  border-radius:16px;
  overflow:hidden;
  border:1px solid ${brand.neutralBorder};
  box-shadow:0 20px 50px rgba(0,0,0,.3);
  display:grid;
  grid-template-rows:auto 1fr auto;
  max-height:90vh;
}
.modal-head, .modal-foot{
  padding:16px 20px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  flex-wrap:wrap;
}
.modal-body{
  padding:0 20px 20px;
  display:grid;
  gap:12px;
  overflow:hidden;
}
.scroll{
  max-height:50vh;
  overflow:auto;
  border:1px solid ${brand.neutralBorder};
  border-radius:10px;
  padding:16px;
  background:#fafafa;
}
.accept-row{ display:grid; gap:10px; }
.checkline{
  display:flex;
  align-items:flex-start;
  gap:10px;
}
    `}</style>
  );
}